<template>
  <div class="about-settings">
    <div class="about-card">
      <div class="about-header">
        <div class="app-logo">
          <img src="@/assets/logo.png" alt="App Logo" class="logo-image" />
        </div>
        <div class="app-info">
          <h2 class="app-name">{{ $t('settings.about.appName') }}</h2>
          <p class="app-slogan">{{ $t('settings.about.appSlogan') }}</p>
        </div>
      </div>

      <el-divider />

      <div class="info-section">
        <div class="info-title">{{ $t('settings.about.systemInformation') }}</div>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">{{ $t('settings.about.applicationName') }}</span>
            <span class="info-value">{{ systemInfo.appName }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">{{ $t('settings.about.version') }}</span>
            <span class="info-value">{{ systemInfo.version }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">{{ $t('settings.about.buildNumber') }}</span>
            <span class="info-value">{{ systemInfo.buildNumber }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">{{ $t('settings.about.environment') }}</span>
            <span class="info-value">{{ systemInfo.environment }}</span>
          </div>
          <div class="info-item full-width">
            <span class="info-label">{{ $t('settings.about.releaseDate') }}</span>
            <span class="info-value">{{ systemInfo.releaseDate }}</span>
          </div>
        </div>
      </div>

      <el-divider />

      <div class="info-section">
        <div class="info-title">{{ $t('settings.about.companyInformation') }}</div>
        <div class="info-grid">
          <div class="info-item full-width">
            <span class="info-label">{{ $t('settings.about.companyName') }}</span>
            <span class="info-value highlight">{{ systemInfo.companyName }}</span>
          </div>
          <div class="info-item full-width">
            <span class="info-label">{{ $t('settings.about.website') }}</span>
            <a :href="systemInfo.website" target="_blank" rel="noopener noreferrer" class="info-link">
              {{ systemInfo.website }}
            </a>
          </div>
          <div class="info-item full-width">
            <span class="info-label">{{ $t('settings.about.email') }}</span>
            <a :href="'mailto:' + systemInfo.email" class="info-link">
              {{ systemInfo.email }}
            </a>
          </div>
        </div>
      </div>

      <el-divider />

      <div class="info-section">
        <div class="info-title">{{ $t('settings.about.legal') }}</div>
        <div class="info-grid">
          <div class="info-item full-width">
            <span class="info-label">{{ $t('settings.about.copyright') }}</span>
            <span class="info-value">{{ systemInfo.copyright }}</span>
          </div>
          <div class="info-item full-width">
            <span class="info-label">{{ $t('settings.about.allRightsReserved') }}</span>
            <span class="info-value">{{ systemInfo.allRightsReserved }}</span>
          </div>
        </div>
      </div>

      <el-divider />

      <div class="about-footer">
        <p class="about-description">
          {{ $t('settings.about.footerDescription') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

import { useI18n } from 'vue-i18n'
import packageJson from '../../../../package.json'

const { t } = useI18n()

const systemInfo = computed(() => ({
  appName: t('settings.about.appName'),
  version: packageJson.version,
  buildNumber: '20251211',
  environment: import.meta.env.MODE || 'production',
  releaseDate: t('settings.about.releaseDate'),
  companyName: t('settings.about.companyNameValue'),
  website: 'https://www.yolal.cn',
  email: 'support@yolal.cn',
  copyright: t('settings.about.copyright'),
  allRightsReserved: t('settings.about.allRightsReserved')
}))
</script>

<style scoped lang="scss">
.about-settings {
  padding: 0;

  .about-card {
    background: var(--bg-color-white, #fff);
    border-radius: 20px;
    padding: 28px;
    box-shadow: 
      0 4px 20px rgba(0, 0, 0, 0.06),
      0 1px 3px rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(102, 126, 234, 0.08);

    @media (max-width: 768px) {
      padding: 20px;
      border-radius: 16px;
    }

    .about-header {
      display: flex;
      align-items: center;
      gap: 20px;
      padding: 16px 0;
      text-align: left;

      @media (max-width: 480px) {
        flex-direction: column;
        text-align: center;
      }

      .app-logo {
        width: 88px;
        height: 88px;
        border-radius: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        overflow: hidden;
        transition: all 0.3s ease;
        position: relative;

        .logo-image {
          width: 85%;
          height: 85%;
          object-fit: cover;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 12px;
          padding: 6px;
          filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.12));
        }
      }

      .app-info {
        .app-name {
          margin: 0 0 8px 0;
          font-size: 26px;
          font-weight: 700;
          color: var(--text-primary, #1f2937);
          line-height: 1.15;
          letter-spacing: -0.5px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;

          @media (max-width: 768px) {
            font-size: 22px;
          }
        }

        .app-slogan {
          margin: 0;
          font-size: 14px;
          color: var(--text-secondary, #6b7280);
          font-weight: 400;
          letter-spacing: 0.3px;
        }
      }
    }

    .el-divider {
      margin: 20px 0;

      @media (max-width: 768px) {
        margin: 16px 0;
      }
    }

    .info-section {
      .info-title {
        font-size: 16px;
        font-weight: 600;
        color: var(--text-primary, #303133);
        margin-bottom: 16px;
        display: flex;
        align-items: center;
        gap: 8px;

        &::before {
          content: '';
          width: 4px;
          height: 16px;
          background: #409eff;
          border-radius: 2px;
        }
      }

      .info-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 16px;

        @media (max-width: 576px) {
          grid-template-columns: 1fr;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 12px;
          background: var(--bg-color-light, #f5f7fa);
          border-radius: 8px;
          transition: all 0.3s ease;

          &:hover {
            background: var(--bg-color-page, #f0f2f5);
          }

          &.full-width {
            grid-column: 1 / -1;
          }

          .info-label {
            font-size: 12px;
            color: var(--text-secondary, #909399);
            font-weight: 500;
          }

          .info-value {
            font-size: 14px;
            color: var(--text-primary, #303133);
            font-weight: 500;

            &.highlight {
              color: #409eff;
              font-weight: 600;
              font-size: 15px;
            }
          }

          .info-link {
            font-size: 14px;
            color: #409eff;
            text-decoration: none;
            font-weight: 500;
            transition: color 0.3s ease;

            &:hover {
              color: #66b1ff;
              text-decoration: underline;
            }
          }
        }
      }
    }

    .about-footer {
      padding-top: 8px;
      
      .about-description {
        font-size: 14px;
        color: var(--text-secondary, #909399);
        text-align: center;
        line-height: 1.6;
        font-style: italic;
        margin: 0;
      }
    }
  }
}

html.dark {
  .about-settings {
    .about-card {
      background: var(--bg-color-white, #1d1d1d);

      .about-header {
        .app-info {
          .app-name {
            color: var(--text-primary, #e4e7ed);
          }
        }
      }

      .info-section {
        .info-title {
          color: var(--text-primary, #e4e7ed);
        }

        .info-grid {
          .info-item {
            background: var(--bg-color-base, #2d2d2d);

            &:hover {
              background: var(--bg-color-light, #3d3d3d);
            }

            .info-label {
              color: var(--text-secondary, #a0aec0);
            }

            .info-value {
              color: var(--text-primary, #e4e7ed);
            }
          }
        }
      }
    }
  }
}
</style>
